import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus, Edit2, Trash2, MapPin, Download, Printer } from 'lucide-react';
import { DataStore, SiteVisit } from '@/lib/data-store';
import { SiteVisitForm } from '@/components/SiteVisitForm';
import { ProjectBreadcrumb } from '@/components/ProjectBreadcrumb';
import { useProject } from '@/context/ProjectContext';
import { generateSiteVisitNumber, getNextSiteVisitNumber } from '@/lib/site-visit-utils';
import { exportToExcel, printTable } from '@/lib/export-utils';
import { printWithLetterhead, generateTableHTML } from '@/lib/print-utils';

const SiteVisitPage = () => {
  const data = DataStore.getData();
  const [visits, setVisits] = useState<SiteVisit[]>(data.siteVisits);
  const [customers] = useState(data.customers);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { selectedProject, setSelectedProject } = useProject();

  const handleAddNew = () => {
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setShowForm(true);
  };

  const handleSubmit = (data: SiteVisit) => {
    const newVisits = editingId
      ? visits.map(v => v.id === editingId ? { ...data, id: editingId } : v)
      : [...visits, data];

    setVisits(newVisits);
    DataStore.saveData({ ...DataStore.getData(), siteVisits: newVisits });
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    const filtered = visits.filter(v => v.id !== id);
    setVisits(filtered);
    DataStore.saveData({ ...DataStore.getData(), siteVisits: filtered });
  };

  const filteredVisits = useMemo(() => visits.filter(v => {
    const matchesSearch = v.projek.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          v.pelanggan.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          v.lokasi.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProject = !selectedProject || v.projek === selectedProject;
    return matchesSearch && matchesProject;
  }), [visits, searchTerm, selectedProject]);

  const nextVisitNo = generateSiteVisitNumber(getNextSiteVisitNumber(visits));

  return (
    <div className="space-y-6">
      {selectedProject && (
        <ProjectBreadcrumb projectName={selectedProject} currentPage="sitevisit" />
      )}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">2. Lawatan Tapak</h1>
          <p className="text-slate-600 mt-1">Urus kunjungan lokasi projek</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {visits.length > 0 && (
            <>
              <Button 
                onClick={() => {
                  const tableHTML = generateTableHTML(filteredVisits, [
                    { label: 'No. Kunjungan', key: 'no' },
                    { label: 'Projek', key: 'projek' },
                    { label: 'Lokasi', key: 'lokasi' },
                    { label: 'Tarikh', key: 'tarikh' },
                    { label: 'Petugas', key: 'petugas' },
                    { label: 'Tujuan', key: 'tujuanKunjungan' },
                    { label: 'Status', key: 'status' }
                  ]);
                  printWithLetterhead('DAFTAR LAWATAN TAPAK', tableHTML, 'LAWATAN TAPAK / SITE VISIT');
                }} 
                className="bg-green-600 hover:bg-green-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Printer size={18} />
                <span className="font-semibold">Cetak</span>
              </Button>
              <Button 
                onClick={() => exportToExcel(filteredVisits, 'Lawatan Tapak')} 
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Download size={18} />
                <span className="font-semibold">Excel</span>
              </Button>
            </>
          )}
          <Button className="bg-red-600 hover:bg-red-700 gap-2 px-4 py-2 h-auto" onClick={handleAddNew}>
            <Plus size={18} />
            <span className="font-semibold">Tambah Kunjungan</span>
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-slate-400" size={20} />
            <Input 
              placeholder="Cari kunjungan tapak..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-slate-300"
            />
          </div>
        </CardContent>
      </Card>

      {/* Visits Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daftar Lawatan Tapak</CardTitle>
          <CardDescription>Jumlah {filteredVisits.length} kunjungan</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredVisits.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Tiada data lawatan tapak. Sila tambahkan kunjungan baru.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table id="visits-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">No. Kunjungan</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Projek</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Lokasi</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Tarikh</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Petugas</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Tujuan</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVisits.map((visit) => (
                    <tr key={visit.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedProject(visit.projek)}>
                      <td className="py-3 px-4 font-medium text-slate-900">{visit.no}</td>
                      <td className="py-3 px-4 text-slate-600 font-medium text-blue-600 hover:underline">{visit.projek}</td>
                      <td className="py-3 px-4 flex items-center gap-2 text-slate-600">
                        <MapPin size={16} className="text-red-500" />
                        {visit.lokasi}
                      </td>
                      <td className="py-3 px-4 text-slate-600">{visit.tarikh}</td>
                      <td className="py-3 px-4 text-slate-600">{visit.petugas}</td>
                      <td className="py-3 px-4 text-xs text-slate-600">{visit.tujuanKunjungan}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                          visit.status === 'Selesai' ? 'bg-green-100 text-green-700' :
                          visit.status === 'Draf' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {visit.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 flex gap-2">
                        <button onClick={() => handleEdit(visit.id)} className="text-blue-600 hover:text-blue-700 text-sm gap-1 flex items-center">
                          <Edit2 size={14} /> Sunting
                        </button>
                        <button onClick={() => handleDelete(visit.id)} className="text-red-600 hover:text-red-700 text-sm gap-1 flex items-center">
                          <Trash2 size={14} /> Padam
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Site Visit Form Modal */}
      {showForm && (
        <SiteVisitForm
          visit={editingId ? visits.find(v => v.id === editingId) : undefined}
          noKunjungan={editingId ? (visits.find(v => v.id === editingId)?.no || nextVisitNo) : nextVisitNo}
          customers={customers}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
};

export default SiteVisitPage;